import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import CareLayout from './CareLayout';
import { care, fail } from './careApi';
import { Btn, Card, Input, PageHeader, Select, Loading } from './careUi';

const EMPTY = {
  name: '', nric: '', phone: '', email: '', dob: '', gender: '', address: '', admission_date: '', room: '', monthly_amount: 0,
  representative_name: '', representative_relationship: '', representative_ic: '', representative_phone: '', representative_email: '',
};

export const ResidentFields = ({ f, set, publicMode }) => {
  const b = (k, label, p = {}) => <Input label={label} value={f[k] ?? ''} onChange={(e) => set({ ...f, [k]: e.target.value })} testid={`reg-${k}`} {...p} />;
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Resident information</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {b('name', 'Resident name *')}{b('nric', 'NRIC / Passport no. *')}{b('dob', 'Date of birth', { type: 'date' })}
          <Select label="Gender" value={f.gender} onChange={(e) => set({ ...f, gender: e.target.value })} testid="reg-gender" options={[{ value: '', label: 'Select' }, 'Male', 'Female']} />
          {b('phone', 'Contact no.')}{b('email', 'Email')}
          {b('address', 'Address', { className: 'sm:col-span-2 lg:col-span-3' })}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Admission</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {b('admission_date', publicMode ? 'Expected admission date' : 'Admission date', { type: 'date' })}
          {!publicMode && b('room', 'Room assignment')}
          {!publicMode && b('monthly_amount', 'Monthly billing amount (RM)', { type: 'number', min: 0, step: '0.01' })}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Primary representative</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {b('representative_name', 'Name')}{b('representative_relationship', 'Relationship to resident')}{b('representative_ic', 'I/C no.')}
          {b('representative_phone', 'Contact number')}{b('representative_email', 'Email (for invoices & receipts)')}
        </div>
      </div>
    </div>
  );
};

export default function CareResidentForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(id ? null : EMPTY);
  const [busy, setBusy] = useState('');
  useEffect(() => { if (id) care.get(`/residents/${id}`).then((r) => setF(r.data)).catch(fail); }, [id]);

  const save = async (submit) => {
    if (!f.name.trim() || !f.nric.trim()) return toast.error('Resident name and NRIC/passport number are required');
    setBusy(submit ? 'submit' : 'save');
    try {
      const body = { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]])), monthly_amount: Number(f.monthly_amount) || 0 };
      const { data } = id ? await care.put(`/residents/${id}`, body) : await care.post('/residents', body);
      if (submit && data.status === 'Draft') await care.post(`/residents/${data.id}/submit`);
      toast.success(submit ? 'Registration submitted for approval' : 'Saved as draft');
      nav(`/care/residents/${data.id}`);
    } catch (e) { fail(e); } finally { setBusy(''); }
  };

  return (
    <CareLayout>
      <PageHeader title={id ? 'Edit registration' : 'New registration'} sub="A Resident ID is generated automatically when you save.">
        <Btn variant="ghost" onClick={() => nav(-1)}>Cancel</Btn>
      </PageHeader>
      {!f ? <Loading /> : (
        <Card className="p-5 sm:p-6">
          <ResidentFields f={f} set={setF} />
          <div className="flex flex-wrap justify-end gap-2 mt-8 pt-5 border-t border-slate-100">
            <Btn variant="ghost" loading={busy === 'save'} onClick={() => save(false)} data-testid="reg-save-draft">Save draft</Btn>
            {(!id || f.status === 'Draft') && <Btn loading={busy === 'submit'} onClick={() => save(true)} data-testid="reg-submit">Submit for approval</Btn>}
          </div>
        </Card>
      )}
    </CareLayout>
  );
}
